import { toDisplayValue } from '../../lib/productFieldValues';
import type { OrderItem, OrderItemFieldAnswer } from '../../types/order';

/**
 * Renders the customer's answers to a product's custom fields for a single
 * order item (e.g. an "Add phone number" field filled at checkout).
 *
 * The backend surfaces these under `item.fields` (mapping fieldId -> answer).
 * Each answer is normally an object `{ title, value, ... }`, but very old
 * orders may store a raw string, so both shapes are handled.
 */
export function OrderItemFields({ fields }: { fields?: OrderItem['fields'] }) {
  if (!fields) return null;

  const entries = Object.entries(fields)
    .map(([key, raw]) => {
      const f = (typeof raw === 'string' ? ({ value: raw } as OrderItemFieldAnswer) : raw) || {};
      // Older orders sometimes saved the raw field id as the title (e.g. an
      // all-digit string, or the field key itself). That's noise, not a label —
      // treat it as "no title" and show just the value. Legacy always printed
      // "title: value" verbatim, which surfaced these junk ids.
      const hasRealTitle = !!f.title && !/^\d+$/.test(f.title.trim()) && f.title !== key;
      // multiSelect answers carry several ticked options — print them comma separated.
      return { title: hasRealTitle ? f.title : '', value: toDisplayValue(f.value) };
    })
    // Drop rows with neither a real label nor a value.
    .filter((f) => !!f.title || !!f.value);

  if (entries.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-0.5">
      {entries.map((f, idx) => (
        <p key={idx} className="text-xs text-text-muted">
          {f.title ? <span className="text-text-secondary">{f.title}: </span> : null}
          <span className="text-text-primary">{f.value}</span>
        </p>
      ))}
    </div>
  );
}
